import React, { useState } from 'react';
import { Crosshair, Sliders, CheckCircle } from 'lucide-react';
import GphRichBlock from '../GphRichBlock';
import { mapSymptomToDrillGoal, SYMPTOM_ALLOWED_CATEGORIES } from '../../../services/gph/gphCoherenceGuard';

const SYMPTOM_DATA = {
  sobe: {
    label: 'Mira Sobe no Spray',
    diagnosis: 'Controle vertical acima do limite físico de arrasto.',
    adjustment: 'Reduza 3 a 6 pontos no ADS Giroscópio (ou suba 2 a 4 no ADS Câmera).',
    test: '3 sprays de 30 balas na parede a 15m, mantendo pegada firme.'
  },
  cai: {
    label: 'Mira Cai Rápido Demais',
    diagnosis: 'Sensibilidade vertical excessiva ou puxada inicial brusca.',
    adjustment: 'Reduza 4 a 8 pontos no ADS Giroscópio e estabilize a rotação inicial.',
    test: 'Dispare rajadas curtas (5 a 10 tiros) focando no ponto central.'
  },
  treme: {
    label: 'Mira Treme para os Lados',
    diagnosis: 'Instabilidade horizontal por sensibilidade geral muito alta.',
    adjustment: 'Reduza 3 a 5 pontos no Giroscópio geral da mira e 3ª Pessoa.',
    test: 'Acompanhe um alvo móvel horizontal a 20m com a mira aberta.'
  },
  passa: {
    label: 'Mira Passa do Alvo no Flick',
    diagnosis: 'Transição rápida (Câmera geral ou Giroscópio inicial) veloz demais.',
    adjustment: 'Reduza 5 a 10 pontos na sensibilidade de Câmera da mira em uso.',
    test: 'Alterne a mira rapidamente entre dois alvos fixos distantes.'
  },
  nao_acompanha: {
    label: 'Mira não Acompanha o Inimigo',
    diagnosis: 'Tracking pesado, exigindo esforço físico ou rotação extrema.',
    adjustment: 'Suba 3 a 7 pontos no Câmera e ADS Giroscópio para ganho de agilidade.',
    test: 'Faça strafe lateral mantendo a retícula travada em um alvo fixo.'
  },
  nao_sei: {
    label: 'Instabilidade Geral de Mira',
    diagnosis: 'Desalinhamento técnico entre sensibilidade de Câmera e ADS.',
    adjustment: 'Redefina para curva intermediária padrão e calibre primeiro o Red Dot.',
    test: 'Tracking contínuo de 15 segundos antes de atirar em rajadas.'
  }
};

export default function SensitivitySkillAdapter({ agent, context = {} }) {
  const [symptom, setSymptom] = useState(context.symptom || 'sobe');
  const currentSymptom = SYMPTOM_DATA[symptom] || SYMPTOM_DATA.sobe;

  return (
    <GphRichBlock
      eyebrow="Diagnóstico"
      title="DIAGNÓSTICO DE SENSIBILIDADE"
      subtitle="O ajuste certo para o problema que você me descreveu."
    >
      <div className="space-y-6 pt-2 text-left">
        {/* Symptom Detection Details */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold tracking-widest text-lime-400 uppercase flex items-center gap-2">
              <Crosshair size={14} />
              Sintoma: {currentSymptom.label}
            </span>
            <span className="text-[10px] font-mono text-slate-500 uppercase">
              {agent.host.canonicalGame === 'free_fire' ? 'Free Fire' : 'PUBG Mobile'}
            </span>
          </div>
          <div className="border-l-2 border-slate-700 pl-4 py-1">
            <p className="text-sm text-slate-300 leading-relaxed m-0 font-sans">
              <strong className="text-white font-medium">Diagnóstico:</strong> {currentSymptom.diagnosis}
            </p>
          </div>
        </div>

        {/* Recommended Adjustment */}
        <div className="space-y-2">
          <h4 className="text-[11px] font-bold tracking-widest text-slate-400 uppercase flex items-center gap-2">
            <Sliders size={14} />
            Ajuste Recomendado
          </h4>
          <div className="text-base font-bold text-lime-300 pl-4 py-1 border-l-2 border-lime-400/50">
            {currentSymptom.adjustment}
          </div>
        </div>

        {/* Practice Protocol */}
        <div className="space-y-2">
          <h4 className="text-[11px] font-bold tracking-widest text-slate-400 uppercase flex items-center gap-2">
            <CheckCircle size={14} />
            Teste Prático
          </h4>
          <p className="text-sm text-slate-300 leading-relaxed pl-4 m-0 border-l-2 border-slate-700">
            {currentSymptom.test}
          </p>
        </div>

        {/* Symptom Selection Selector */}
        <div className="space-y-3 pt-2">
          <span className="text-[10px] font-bold tracking-wider text-slate-500 uppercase block">Refinar para outro sintoma:</span>
          <div className="flex flex-wrap gap-2">
            {Object.entries(SYMPTOM_DATA).map(([key, data]) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setSymptom(key);
                  agent.updateContext?.('lastDiagnosis', {
                    symptom: key,
                    game: agent.host.canonicalGame,
                    at: new Date().toISOString()
                  });
                }}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  symptom === key
                    ? 'bg-lime-500/10 text-lime-300 border border-lime-500/40 shadow-sm'
                    : 'bg-transparent text-slate-400 border border-white/5 hover:border-white/10 hover:text-slate-300'
                }`}
              >
                {data.label.split(' ')[1] || key}
              </button>
            ))}
          </div>
        </div>

        {/* Action Options */}
        <div className="border-t border-white/5 pt-5 mt-6 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
          <span className="text-[10px] font-bold tracking-wider text-slate-500 uppercase">Próximos Passos:</span>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              className="gph-btn gph-btn--primary text-xs animate-fade-in"
              onClick={() => agent.runCapability('mi_analysis_aim', { fromDiagnosis: symptom })}
            >
              Validar com vídeo
            </button>
            <button
              type="button"
              className="gph-btn gph-btn--ghost text-xs animate-fade-in"
              onClick={() => agent.runCapability('training_single_drill', {
                drillGoal: mapSymptomToDrillGoal(symptom),
                allowedCategories: SYMPTOM_ALLOWED_CATEGORIES[symptom] || null,
                fromDiagnosis: symptom
              })}
            >
              Gerar treino
            </button>
            <button
              type="button"
              className="gph-btn gph-btn--ghost text-xs animate-fade-in"
              onClick={() => agent.runCapability('calculator_pubg', {})}
            >
              Calcular miras
            </button>
          </div>
        </div>
      </div>
    </GphRichBlock>
  );
}
